import React, { useState, useEffect, useRef } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import NewMemoryModal from './NewMemoryModal'
import { DisplayImage } from '../../../widgets/components/General/DisplayImage'
import clsx from 'clsx'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { deleteAiMemory, getAiMemories } from '../../../profile/core/_requests'
import { useAuth } from '../../../auth'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'
import { useTimeZone } from '../../../../hooks/useTimeZone'
import { useDateFormat } from '../../../../hooks/useDateFormat'
import { useThemeMode } from '../../../../../_metronic/partials'
import { DataTableFilter } from './DataTableFilter'
import { NoDataPage } from '../../../resources/components/NoDataPage'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
// import { GTMEvent } from '../../../hooks/gtmHelpers'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface AIMemory {
  id: number
  memoryId: string
  memoryName: string
  memoryText: string
  createdBy: string
  createdAt: string
  profileImg: string
}

export const DataTableAIMemory = ({ aiMemoryFor }: { aiMemoryFor: string }) => {
  const { formatMessage } = useIntl()
  const [isActive, setIsActive] = useState(false)
  const [aiMemoryFilter, setAiMemoryFilter] = useState(false) //eslint-disable-line
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [, setShowKebabMenu] = useState(false)
  const [expandedYourShareholdings, setExpandedYourShareholdings] = useState<number | null>(null)
  const { mode } = useThemeMode()
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })

  const [aiMemories, setAiMemories] = useState<any[]>([])
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const { companyId, currentUser } = useAuth()
  const [memoryId, setMemoryId] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false) //eslint-disable-line
  const [clear, setClear] = useState(true) //eslint-disable-line
  const { getTimeZoneValue } = useTimeZone()
  const { getDateValue } = useDateFormat()
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }
  const [date, setDate] = useState<string>('') //eslint-disable-line

  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedYourShareholdings(expandedYourShareholdings === rowIndex ? null : rowIndex)
  }

  const columns = React.useMemo<ColumnDef<AIMemory, any>[]>(
    () => [
      {
        accessorKey: 'memoryName',
        id: 'memoryName',
        header: 'Memory Name',
        cell: (info) => `${info.getValue()}`,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdBy',
        id: 'createdBy',
        header: 'Created By',
        cell: (info) => {
          const profileImg = info.row.original.profileImg || ''
          return (
            <div className='d-flex align-items-center gap-2 px-4'>
              <DisplayImage
                imgName={profileImg}
                alt='profile pic'
                width={24}
                height={24}
                className='rounded-circle'
              />
              <div>
                <h5 className='m-0 fw-normal font-size-13'>{info.getValue()}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Created on Date & Time',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </>
          )
        },
        filterFn: 'includesString',
      },
    ],
    [getDateValue, getTimeZoneValue]
  ) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const table = useReactTable({
    data: aiMemories,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    // globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const getAIMemories = async () => {
    setLoading(true)
    try {
      const {
        data: { success, data },
      } = await getAiMemories(
        aiMemoryFor,
        pagination.currentPage,
        pagination.pageSize,
        globalFilter,
        date,
        aiMemoryFor === 'company' ? companyId : currentUser?.userId
      )

      if (success) {
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))

        setAiMemories(
          data.aiMemories.map((memory: any, index: number) => ({
            id: index,
            memoryId: memory.memoryId,
            memoryName: memory?.memoryName,
            createdBy: memory?.user?.email,
            createdAt: memory?.createdAt,
            memoryText: memory?.memoryText,
            profileImg: memory?.user?.file?.name,
          }))
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAIMemories()
  }, [pagination.currentPage, pagination.pageSize, globalFilter, aiMemoryFor, date]) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteMemory = async () => {
    setDeleteLoading(true)
    try {
      const {
        data: { success, errors },
      } = await deleteAiMemory(memoryId)
      if (success) {
        toast.success(formatMessage({ id: 'Memory Deleted' }))
        getAIMemories()
        setMemoryId(null)
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDeleteLoading(true)
    }
  }

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  return (
    <>
      <div className='d-flex flex-md-row flex-column align-items-md-center gap-2 mb-4'>
        <div className='d-flex justify-content-between w-100 gap-1 height-36'>
          <div className='h-100 border bg-white d-flex rounded'>
            <button
              className={clsx(
                'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
              )}
              id='fc-toogle'
              title='Sort and Filter'
              data-bs-toggle='tooltip'
              data-bs-placement='left'
              data-bs-dismiss='click'
              data-bs-trigger='hover'
              onClick={() => {
                setAiMemoryFilter(true)
              }}
            >
              <KTSVG
                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                className='svg-icon-6 svg-icon-muted me-1'
              />
              <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
              {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
            </button>
          </div>
          <form className='align-items-center w-100 w-md-auto d-flex'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <input
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder='Search'
            />
          </form>
        </div>
        <div className='d-flex gap-1 flex-shrink-0'>
          <button
            className='btn text-white custom-button-property bg-primary height-36 d-flex align-items-center font-size-12 w-100 justify-content-center '
            onClick={() => {
              setIsActive(true)
              setMemoryId(null)
            }}
          >
            {formatMessage({ id: 'Add a Memory' })}
          </button>
          <NewMemoryModal
            isActive={isActive}
            setIsActive={setIsActive}
            aiMemoryFor={aiMemoryFor}
            memoryId={memoryId}
            setMemoryId={setMemoryId}
            getAIMemories={getAIMemories}
          />
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!loading && table.getRowModel().rows.length === 0 ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-0 px-4'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr
                      key={row.id}
                      onClick={() => handleYourRowToggle(index)}
                      className={`border-bottom ${expandedYourShareholdings === index
                          ? 'expanded-row-captable bg-light'
                          : 'bg-white'
                        }`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <>
                          {cellIndex === 0 ? (
                            <td key={cell.id} className='pt-6 px-4 border-0'>
                              <div className='d-flex gap-2'>
                                <p
                                  className='m-0'
                                  style={{
                                    transform:
                                      expandedYourShareholdings === index
                                        ? 'rotate(90deg)'
                                        : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease',
                                  }}
                                >
                                  &gt;
                                </p>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            </td>
                          ) : (
                            <td
                              key={cell.id}
                              className={`${cellIndex === 1 ? 'pt-5' : 'pt-6'} px-4 border-0`}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          )}
                        </>
                      ))}
                      <td
                        className='pt-5 px-4'
                        onClick={(e) => {
                          e.stopPropagation()
                          setMemoryId(row.original.memoryId)
                        }}
                      >
                        <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                          <Dropdown
                            show={index === selectedIndex}
                            onToggle={(isOpen, event) => {
                              if (event.source === 'click') {
                                setSelectedIndex(isOpen ? index : undefined)
                              } else {
                                setSelectedIndex(undefined)
                              }
                            }}
                          >
                            <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                              <img src={threeDots} alt='' className='three-dots-image' />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='m-2'>
                              <Dropdown.Item
                                className='text-dark ps-5'
                                onClick={() => setIsActive(true)}
                              >
                                <p className='mb-0'>{formatMessage({ id: 'Edit' })}</p>
                              </Dropdown.Item>
                              <Dropdown.Item
                                className='text-danger ps-5'
                                onClick={() => deleteMemory()}
                              >
                                {formatMessage({ id: 'Delete' })}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                    {expandedYourShareholdings === index && row.original && (
                      <>
                        <tr
                          className={`${expandedYourShareholdings === index
                              ? 'expanded-row-captable bg-light'
                              : 'bg-white'
                            }`}
                        >
                          <td colSpan={3} className='min-w-120px pb-6 pt-4 ps-4'>
                            <p className='ms-4'>{formatMessage({ id: 'Memory Text' })}</p>
                            <p className='font-size-12 ms-4'>
                              {row.original.memoryText ||
                                formatMessage({ id: 'No memory available' })}
                            </p>
                          </td>
                          <td></td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>
      {/* <UpdateQueueModal isActive={isModalActive} setIsActive={setIsModalActive} /> */}
      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
        {aiMemories?.length > 0 && pagination.totalCount > 10 && (
          <div className='w-100 d-flex justify-content-between align-items-center px-0 mt-8'>
            <div className='d-flex gap-2 w-100 align-items-center justify-content-start mb-4'>
              <select
                aria-label='Default select example'
                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark ${mode === 'dark' ? ' border-dark' : ''
                  }`}
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                      ? 1
                      : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.nextPage ? aiMemories.length : pagination.totalCount} of ${pagination.totalCount
                    } records`,
                })}
              </span>
            </div>

            {/* <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {Array.from({length: pagination.totalPages}, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination> */}
            <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {(() => {
                const items = [];
                const { currentPage, totalPages } = pagination;
                const pageNeighbours = 1; // number of pages to show on each side of current

                const startPage = Math.max(2, currentPage - pageNeighbours);
                const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

                // Always show first page
                items.push(
                  <Pagination.Item
                    key={1}
                    active={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>
                );

                // Show left ellipsis if needed
                if (startPage > 2) {
                  items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                }

                // Show middle page numbers
                for (let i = startPage; i <= endPage; i++) {
                  items.push(
                    <Pagination.Item
                      key={i}
                      active={i === currentPage}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  );
                }

                // Show right ellipsis if needed
                if (endPage < totalPages - 1) {
                  items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                }

                // Always show last page if totalPages > 1
                if (totalPages > 1) {
                  items.push(
                    <Pagination.Item
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  );
                }

                return items;
              })()}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>
          </div>
        )}
      </div>
      <DataTableFilter
        filter={aiMemoryFilter}
        setFilter={setAiMemoryFilter}
        setClear={setClear}
        setDate={setDate}
      />
    </>
  )
}
